import { useNavigate } from "react-router-dom";
import { Banknote, Facebook, Heart, Instagram, Mail, MapPin, MessageCircle, Phone } from "lucide-react";
import { imgUrl } from "@/lib/api";
import { useSite, waLinkFor } from "@/lib/SiteContext";

export default function Footer({ onNavigate, onSelectCategory }) {
  const { brand, contact, quickLinks, categories } = useSite();
  const navigate = useNavigate();

  const go = (url) => {
    if (url.startsWith("#")) onNavigate(url);
    else if (url.startsWith("/")) navigate(url);
    else window.open(url, "_blank", "noopener");
  };

  const igUrl = contact.instagramUrl || contact.instagram_url;
  const fbUrl = contact.facebookUrl || contact.facebook_url;

  return (
    <footer className="bg-ink pb-10 pt-20 text-white/70">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-2">
              {brand.logo ? (
                <img src={imgUrl(brand.logo)} alt={brand.name} className="h-9 w-auto object-contain" />
              ) : (
                <Heart size={20} className="fill-rose text-rose" />
              )}
              <span className="font-serif text-3xl font-semibold text-cream">
                {brand.name}
              </span>
            </div>
            <p className="mt-3 font-serif text-lg italic text-rose">
              {brand.tagline}
            </p>
            <p className="mt-5 max-w-sm text-sm leading-relaxed">
              {brand.description}
            </p>
            <div className="mt-7 flex gap-3">
              {[
                { icon: Instagram, href: igUrl, label: "Instagram", testid: "footer-instagram-link" },
                { icon: MessageCircle, href: waLinkFor(contact), label: "WhatsApp", testid: "footer-whatsapp-link" },
                { icon: Facebook, href: fbUrl, label: "Facebook", testid: "footer-facebook-link" },
                { icon: Mail, href: `mailto:${contact.email}`, label: "Email", testid: "footer-email-link" },
              ].map((s) => (
                <a
                  key={s.label}
                  data-testid={s.testid}
                  href={s.href}
                  target={s.href?.startsWith("http") ? "_blank" : undefined}
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  className="flex h-11 w-11 items-center justify-center rounded-full border border-white/15 text-white/70 transition-colors duration-300 hover:border-rose hover:bg-rose hover:text-white"
                >
                  <s.icon size={17} />
                </a>
              ))}
            </div>
          </div>

          <div className="lg:col-span-2">
            <h4 className="mb-5 text-xs font-bold uppercase tracking-[0.25em] text-cream">
              Quick Links
            </h4>
            <ul className="flex flex-col gap-3">
              {quickLinks.map((link, i) => (
                <li key={link.id || i}>
                  <button
                    data-testid={`footer-link-${link.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    onClick={() => go(link.url)}
                    className="text-left text-sm transition-colors hover:text-rose"
                  >
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-2">
            <h4 className="mb-5 text-xs font-bold uppercase tracking-[0.25em] text-cream">
              Categories
            </h4>
            <ul className="flex flex-col gap-3">
              {categories.slice(0, 6).map((cat) => (
                <li key={cat.slug || cat.id}>
                  <button
                    data-testid={`footer-category-${cat.slug || cat.id}`}
                    onClick={() => onSelectCategory(cat.slug || cat.id)}
                    className="text-sm transition-colors hover:text-rose"
                  >
                    {cat.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-3">
            <h4 className="mb-5 text-xs font-bold uppercase tracking-[0.25em] text-cream">
              Contact
            </h4>
            <ul className="flex flex-col gap-4 text-sm">
              <li className="flex items-center gap-3">
                <Phone size={15} className="shrink-0 text-rose" />
                <a
                  data-testid="footer-phone"
                  href={`tel:+${(contact.whatsapp || "").replace(/\D/g, "")}`}
                  className="transition-colors hover:text-rose"
                >
                  {contact.phoneDisplay || contact.phone_display}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail size={15} className="shrink-0 text-rose" />
                <a
                  data-testid="footer-email"
                  href={`mailto:${contact.email}`}
                  className="transition-colors hover:text-rose"
                >
                  {contact.email}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Instagram size={15} className="shrink-0 text-rose" />
                <a
                  data-testid="footer-instagram"
                  href={igUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="transition-colors hover:text-rose"
                >
                  @{contact.instagram}
                </a>
              </li>
              {contact.address && (
                <li className="flex items-start gap-3">
                  <MapPin size={15} className="mt-0.5 shrink-0 text-rose" />
                  <a
                    data-testid="footer-address"
                    href={contact.mapsUrl || contact.maps_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="transition-colors hover:text-rose"
                  >
                    {contact.address}
                  </a>
                </li>
              )}
              {contact.payment_note && (
                <li className="flex items-start gap-3">
                  <Banknote size={15} className="mt-0.5 shrink-0 text-rose" />
                  <span>{contact.payment_note}</span>
                </li>
              )}
            </ul>
          </div>
        </div>

        <div className="mt-16 grid gap-4 border-t border-white/10 pt-8 text-center text-xs sm:grid-cols-3 sm:items-center">
          <span className="hidden sm:block" aria-hidden />
          <p data-testid="footer-since-text">{brand.footer_text || `Since 2021 ❤️ ${brand.name}`}</p>
          <p className="flex items-center justify-center gap-1.5 sm:justify-end">
            Made with <Heart size={12} className="fill-rose text-rose" /> in
            India
          </p>
        </div>
      </div>
    </footer>
  );
}
